// V·IAnet Governance — M15 Sign
// Preview de aprobadores (F2.1+F2.2)
//
// Muestra, antes de pulsar "Enviar a aprobación", quién recibirá la solicitud
// resolviendo jerarquía de departamentos + responsable de proceso + fallback.
// Estanqueidad M1↔M15: NO importa nada de modules/governance/structure/. Los datos llegan
// exclusivamente del endpoint GET /sign/aprobadores.

import { useEffect, useState } from 'react';
import { request } from '../../shared/services/api';
import { useI18n } from '../../../i18n';

type FuenteAprobacion = 'jerarquia' | 'proceso' | 'fallback';

interface AprobadorResuelto {
  usuarioId: string;
  nombreCompleto: string | null;
  fuente: FuenteAprobacion;
  nivel: number;
}

export interface AprobadoresPreviewProps {
  entidadTipo: 'version_documento' | 'flujograma';
  entidadId: string;
  /** Si false, el componente no se renderiza. */
  visible?: boolean;
}

export function AprobadoresPreview({
  entidadTipo,
  entidadId,
  visible = true,
}: AprobadoresPreviewProps) {
  const { t } = useI18n();
  const [aprobadores, setAprobadores] = useState<AprobadorResuelto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!visible || !entidadId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    request<{ data: AprobadorResuelto[] }>(
      `/sign/aprobadores?entidadTipo=${encodeURIComponent(entidadTipo)}&entidadId=${encodeURIComponent(entidadId)}`,
    )
      .then((res) => {
        if (cancelled) return;
        setAprobadores(res.data ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entidadTipo, entidadId, visible]);

  if (!visible) return null;

  // Error silencioso: el botón de enviar sigue disponible, simplemente
  // no se muestra el preview. No bloqueamos al usuario.
  if (error) return null;

  if (loading) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          fontSize: 11,
          color: '#6B7280',
        }}
      >
        {/* Keyframe local — no depende de CSS global para evitar degradación visual */}
        <style>{`@keyframes viaSpinApr { to { transform: rotate(360deg); } }`}</style>
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            borderRadius: '50%',
            border: '2px solid #DBEAFE',
            borderTopColor: '#2F5496',
            animation: 'viaSpinApr 0.8s linear infinite',
          }}
        />
        <span
          style={{
            display: 'inline-block',
            width: 120,
            height: 8,
            borderRadius: 4,
            background: 'linear-gradient(90deg, #EFF6FF, #DBEAFE, #EFF6FF)',
          }}
        />
      </div>
    );
  }

  if (!aprobadores || aprobadores.length === 0) return null;

  // Si la única fuente es fallback, mostramos un destinatario genérico.
  const soloFallback = aprobadores.every((a) => a.fuente === 'fallback');
  if (soloFallback) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '2px 8px',
          fontSize: 11,
          color: '#374151',
          flexWrap: 'wrap',
        }}
        title={t.workflow.approvalPreview.willBeSentTo}
      >
        <span style={{ color: '#6B7280' }}>{t.workflow.approvalPreview.willBeSentTo}</span>
        <strong style={{ color: '#1F3864' }}>{t.workflow.approvalPreview.sourceFallback}</strong>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        fontSize: 11,
        color: '#374151',
        flexWrap: 'wrap',
      }}
      title={t.workflow.approvalPreview.willBeSentTo}
    >
      <span style={{ color: '#6B7280' }}>{t.workflow.approvalPreview.willBeSentTo}</span>
      {aprobadores.map((a, idx) => (
        <span key={a.usuarioId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <strong style={{ color: '#1F3864' }}>{a.nombreCompleto ?? '—'}</strong>
          <span style={{ color: '#6B7280' }}>({renderFuente(a.fuente, t)})</span>
          {idx < aprobadores.length - 1 && <span style={{ color: '#9CA3AF' }}>·</span>}
        </span>
      ))}
    </div>
  );
}

function renderFuente(fuente: FuenteAprobacion, t: ReturnType<typeof useI18n>['t']): string {
  if (fuente === 'jerarquia') return t.workflow.approvalPreview.sourceHierarchy;
  if (fuente === 'proceso') return t.workflow.approvalPreview.sourceProcess;
  return t.workflow.approvalPreview.sourceFallback;
}
